import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  currentUserEmail,
  loadTokens,
  performLogout,
  startLogin,
} from "../auth";
import {
  getRealtimeStatus,
  getSyncEmitError,
  subscribeRealtimeStatus,
  subscribeSyncEmitError,
} from "../services/waveSession";
import { useSyncExternalStore } from "react";
import type { WaveRealtimeStatus } from "@vida/wave-core";
import { displayNameFromEmail } from "@vida/wave-core";
import {
  WAVE_APPEARANCE_MODES,
  useWaveTheme,
  waveAppearanceLabel,
} from "../services/waveTheme";
import {
  fetchWaveCredits,
  formatCreditsBalance,
  getAdminApiBaseUrl,
} from "../services/waveAccountAPI";
import { useWaveSyncLog } from "../services/waveSyncLog";
import {
  getWaveSyncDebugVisible,
  setWaveSyncDebugVisible,
  subscribeWaveSyncDebugVisible,
} from "../services/waveSyncDebug";

function realtimeLabel(status: WaveRealtimeStatus): string {
  switch (status) {
    case "online":
      return "Online";
    case "polling":
      return "Sync";
    default:
      return "Offline";
  }
}

function realtimeDotClass(status: WaveRealtimeStatus): string {
  switch (status) {
    case "online":
      return "realtime-dot-inline realtime-online";
    case "polling":
      return "realtime-dot-inline realtime-polling";
    default:
      return "realtime-dot-inline";
  }
}

export function WaveAccountPage() {
  const navigate = useNavigate();
  const loggedIn = Boolean(loadTokens());
  const email = currentUserEmail();
  const displayName = email ? displayNameFromEmail(email) : "Explorer";
  const { mode, setMode } = useWaveTheme();
  const { capturing, entries, setCapturing, copyLog } = useWaveSyncLog();
  const showSyncDebugBar = useSyncExternalStore(
    subscribeWaveSyncDebugVisible,
    getWaveSyncDebugVisible,
    () => false,
  );
  const [creditsBalance, setCreditsBalance] = useState("—");
  const [creditsError, setCreditsError] = useState<string | null>(null);
  const realtimeStatus = useSyncExternalStore(
    subscribeRealtimeStatus,
    getRealtimeStatus,
    () => "offline" as WaveRealtimeStatus,
  );
  const syncEmitError = useSyncExternalStore(
    subscribeSyncEmitError,
    getSyncEmitError,
    () => null,
  );

  useEffect(() => {
    if (!loggedIn || !getAdminApiBaseUrl()) {
      setCreditsBalance("—");
      return;
    }
    void (async () => {
      try {
        const credits = await fetchWaveCredits();
        setCreditsBalance(formatCreditsBalance(credits.balanceCents));
        setCreditsError(null);
      } catch (e) {
        setCreditsBalance("—");
        setCreditsError(e instanceof Error ? e.message : "Crediti non disponibili");
      }
    })();
  }, [loggedIn]);

  if (!loggedIn) {
    return (
      <div className="wave-push-page">
        <header className="wave-push-header">
          <button type="button" className="link-btn" onClick={() => navigate(-1)}>
            ← Indietro
          </button>
          <h1>Account VIDA</h1>
        </header>
        <p className="muted">Accedi per vedere il profilo e i dati cloud.</p>
        <button type="button" className="primary-btn" onClick={() => startLogin()}>
          Accedi a VIDA
        </button>
      </div>
    );
  }

  return (
    <div className="wave-push-page">
      <header className="wave-push-header">
        <button type="button" className="link-btn" onClick={() => navigate(-1)}>
          ← Indietro
        </button>
        <h1>Account VIDA</h1>
      </header>

      <section className="wave-push-section">
        <h2>Profilo</h2>
        <dl className="wave-push-dl">
          <div>
            <dt>Nome</dt>
            <dd>{displayName}</dd>
          </div>
          <div>
            <dt>Email</dt>
            <dd>{email ?? "—"}</dd>
          </div>
        </dl>
      </section>

      <section className="wave-push-section">
        <h2>Aspetto</h2>
        <div className="wave-theme-picker" role="group" aria-label="Tema">
          {WAVE_APPEARANCE_MODES.map((option) => (
            <button
              key={option}
              type="button"
              className={mode === option ? "active" : ""}
              onClick={() => setMode(option)}
            >
              {waveAppearanceLabel(option)}
            </button>
          ))}
        </div>
      </section>

      <section className="wave-push-section">
        <h2>Cloud</h2>
        <dl className="wave-push-dl">
          <div>
            <dt>Realtime</dt>
            <dd>
              <span className={realtimeDotClass(realtimeStatus)} aria-hidden />
              {realtimeLabel(realtimeStatus)}
            </dd>
          </div>
          <div>
            <dt>Crediti VIDA</dt>
            <dd>{creditsBalance}</dd>
          </div>
        </dl>
        {syncEmitError && <p className="invite-feedback error">{syncEmitError}</p>}
        {creditsError && <p className="muted">{creditsError}</p>}
      </section>

      <section className="wave-push-section">
        <h2>Diagnostica sync</h2>
        <label className="wave-sync-log-toggle">
          <input
            type="checkbox"
            checked={showSyncDebugBar}
            onChange={(event) => setWaveSyncDebugVisible(event.target.checked)}
          />
          Barra seq nell&apos;editor
        </label>
        <p className="muted">
          Mostra snapshot seq, next op seq, tail scaricate e blocchi materializzati sopra
          l&apos;editor. Utile per confrontare web e iOS.
        </p>
      </section>

      <section className="wave-push-section">
        <h2>Log sync</h2>
        <label className="wave-sync-log-toggle">
          <input
            type="checkbox"
            checked={capturing}
            onChange={(event) => setCapturing(event.target.checked)}
          />
          Registra diagnostica sync
        </label>
        {entries.length === 0 ? (
          <p className="muted">Nessun log ancora. Apri una wave o esegui operazioni cloud.</p>
        ) : (
          <ul className="wave-sync-log-list">
            {[...entries].reverse().slice(0, 80).map((entry) => (
              <li key={entry.id}>
                <strong>{entry.category}</strong>: {entry.message}
                {entry.detail ? <div className="muted">{entry.detail}</div> : null}
                <time dateTime={new Date(entry.at).toISOString()}>
                  {new Date(entry.at).toLocaleTimeString("it-IT")}
                </time>
              </li>
            ))}
          </ul>
        )}
        {entries.length > 0 && (
          <button type="button" className="link-btn" onClick={() => void copyLog()}>
            Copia log
          </button>
        )}
      </section>

      <button
        type="button"
        className="link-btn wave-push-logout"
        onClick={() => {
          performLogout();
          navigate("/", { replace: true });
        }}
      >
        Esci
      </button>
    </div>
  );
}
